/**
 * API Client pour la détection automatique des dépendances et hiérarchie cloud
 *
 * Fonctionnalités:
 * - Analyse du code Terraform pour détecter les dépendances
 * - Auto-création des ressources manquantes
 * - Gestion de la hiérarchie cloud (Resource Groups, VPCs, Projects)
 */

import api from './client';

/**
 * Interface pour les dépendances détectées
 */
export interface TerraformDependency {
  property: string;
  targetType: string;
  targetNode: string;
  targetName: string;
  attribute?: string;
  type: 'property_reference' | 'explicit_depends_on';
  autoDetected: boolean;
}

/**
 * Interface pour les ressources manquantes
 */
export interface MissingResource {
  type: string;
  name: string;
  reason: string;
  severity: 'high' | 'medium' | 'low';
  defaultProperties: Record<string, any>;
  sourceNodes?: string[];
}

/**
 * Interface pour les suggestions d'architecture
 */
export interface ArchitectureSuggestion {
  type: 'security' | 'network' | 'missing_container';
  severity: 'high' | 'medium' | 'low';
  message: string;
  action: string;
}

/**
 * Interface pour le résultat de l'analyse
 */
export interface DependencyAnalysisResult {
  success: boolean;
  dependencies: TerraformDependency[];
  missingResources: MissingResource[];
  suggestions: ArchitectureSuggestion[];
  suggestedEdges: any[];
  stats?: {
    totalResources: number;
    dependencies: number;
    missing: number;
  };
  message: string;
}

/**
 * Interface pour la validation de hiérarchie
 */
export interface HierarchyValidation {
  success: boolean;
  isValid: boolean;
  missing: Array<{
    type: string;
    reason: string;
    severity: 'high' | 'medium' | 'low';
  }>;
  warnings: Array<{
    type: string;
    parentType: string;
    message: string;
    severity: 'high' | 'medium' | 'low';
  }>;
  message: string;
}

/**
 * Analyse les dépendances Terraform automatiquement
 *
 * @param architectureId - ID de l'architecture
 * @param diagramData - Données du canvas (optionnel)
 * @returns Analyse des dépendances
 */
export async function analyzeDependencies(
  architectureId: string,
  diagramData?: { nodes: any[]; edges: any[] }
): Promise<DependencyAnalysisResult> {
  const response = await api.post('/api/dependencies/analyze', {
    architectureId,
    diagramData
  });

  return response.data;
}

/**
 * Crée automatiquement les ressources manquantes
 *
 * @param architectureId - ID de l'architecture
 * @param diagramData - Données actuelles du canvas
 * @param missingResources - Liste des ressources à créer
 * @returns Données mises à jour avec les nouvelles ressources
 */
export async function autoCreateResources(
  architectureId: string,
  diagramData: { nodes: any[]; edges: any[] },
  missingResources: MissingResource[]
): Promise<{
  success: boolean;
  updatedDiagramData: { nodes: any[]; edges: any[] };
  createdNodes: any[];
  message: string;
}> {
  const response = await api.post('/api/dependencies/auto-create', {
    architectureId,
    diagramData,
    missingResources
  });

  return response.data;
}

/**
 * Initialise la hiérarchie cloud (Resource Groups, VPCs, Projects)
 *
 * @param architectureId - ID de l'architecture
 * @param diagramData - Données du canvas (optionnel)
 * @returns Hiérarchie initialisée avec les containers créés
 */
export async function initializeCloudHierarchy(
  architectureId: string,
  diagramData?: { nodes: any[]; edges: any[] }
): Promise<{
  success: boolean;
  updatedDiagramData: { nodes: any[]; edges: any[] };
  createdNodes: any[];
  message: string;
}> {
  const response = await api.post('/api/dependencies/initialize-hierarchy', {
    architectureId,
    diagramData
  });

  return response.data;
}

/**
 * Valide la hiérarchie cloud de l'architecture
 *
 * @param architectureId - ID de l'architecture
 * @param diagramData - Données du canvas (optionnel)
 * @returns Résultat de la validation
 */
export async function validateHierarchy(
  architectureId: string,
  diagramData?: { nodes: any[]; edges: any[] }
): Promise<HierarchyValidation> {
  const response = await api.post('/api/dependencies/validate-hierarchy', {
    architectureId,
    diagramData
  });

  return response.data;
}

/**
 * Crée automatiquement les containers nécessaires pour une ressource
 *
 * @param architectureId - ID de l'architecture
 * @param resourceNode - Node de la ressource
 * @param diagramData - Données actuelles du canvas
 * @returns Containers créés
 */
export async function createContainersForResource(
  architectureId: string,
  resourceNode: any,
  diagramData: { nodes: any[]; edges: any[] }
): Promise<{
  success: boolean;
  updatedDiagramData: { nodes: any[]; edges: any[] };
  createdContainers: any[];
  message: string;
}> {
  const response = await api.post('/api/dependencies/create-containers', {
    architectureId,
    resourceNode,
    diagramData
  });

  return response.data;
}

export default {
  analyzeDependencies,
  autoCreateResources,
  initializeCloudHierarchy,
  validateHierarchy,
  createContainersForResource
};
